// Collapsible scope controls (mode / environment / tenant / timezone /
// confidential access / web search). Changing any of these starts a new chat — the
// page enforces it. Styled to the SQ-AI tokens (sqAi.module.css).

import React from "react";
import styles from "../sqAi.module.css";
import SQ_APP_TIMEZONES from "../sqAppTimezones.json";

export const ENVS = ["dev", "stage", "prod"];

// The engine's output-mode whitelist (socket.js VALID_MODES) with user-facing
// wording. "general" is the wire name for the default conversational discipline;
// the engine normalizes it back to null, so picking it composes the same prompt
// a mode-less caller gets.
const MODES = [
  { value: "chart", label: "Chart — analytics, breakdowns, listings" },
  { value: "general", label: "General — full conversational assistant" },
  { value: "summarise", label: "Summarise — narrative write-up of one record" },
];

// Exactly the zones a SafetyQube user can pick on their profile in the web app, in its order — generated from its list
// (scripts/sync-sq-app-timezones.js, K-RSM8-1), never typed here, so QA can set the zone their dashboard login uses.
const TIMEZONES = SQ_APP_TIMEZONES.zones;

export default function SettingsBar({
  mode,
  onModeChange,
  env,
  onEnvChange,
  tenants,
  tenantSchema,
  onTenantChange,
  tenantsLoading,
  tenantsError,
  confidentialAccess,
  onConfidentialChange,
  webSearch,
  onWebSearchChange,
  // The engine's ceiling for the current mode, as the SERVER reported it (null until it
  // has). The checkbox may only NARROW that ceiling, so a false ceiling means a tick would
  // change nothing — and a control that looks live and does nothing is worse than a
  // disabled one, so it is disabled with the reason beside it.
  webSearchCeiling,
  userTz,
  onUserTzChange,
  disabled,
}) {
  return (
    <div className={styles.settings}>
      <div className={styles.column}>
        <div className={styles.settingsGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Mode</span>
            <select className={styles.select} value={mode} onChange={(e) => onModeChange(e.target.value)} disabled={disabled}>
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Environment</span>
            <select className={styles.select} value={env} onChange={(e) => onEnvChange(e.target.value)} disabled={disabled}>
              {ENVS.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Tenant</span>
            <select
              className={styles.select}
              value={tenantSchema}
              onChange={(e) => onTenantChange(e.target.value)}
              disabled={disabled || tenantsLoading || tenants.length === 0}
            >
              {tenantsLoading && <option value="">Loading…</option>}
              {!tenantsLoading && tenants.length === 0 && <option value="">No tenants available</option>}
              {!tenantsLoading &&
                tenants.map((t) => {
                  const name = t.name || t.schema || t;
                  return (
                    <option key={name} value={name}>{name}</option>
                  );
                })}
            </select>
            {tenantsError && <span className={styles.fieldError}>{tenantsError}</span>}
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Timezone</span>
            <select className={styles.select} value={userTz} onChange={(e) => onUserTzChange(e.target.value)} disabled={disabled}>
              {!TIMEZONES.includes(userTz) && userTz && <option value={userTz}>{userTz}</option>}
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
              {/* a customer whose profile names no zone is counted in UTC, as the dashboard counts them (K-RB8-2) */}
              <option value="">Not set (UTC)</option>
            </select>
          </label>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Access</span>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={confidentialAccess}
                onChange={(e) => onConfidentialChange(e.target.checked)}
                disabled={disabled}
              />
              Include confidential records
            </label>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Web search</span>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={webSearchCeiling === false ? false : webSearch}
                onChange={(e) => onWebSearchChange(e.target.checked)}
                disabled={disabled || webSearchCeiling === false}
              />
              Allow looking things up on the web
            </label>
            {webSearchCeiling === false && (
              <span className={styles.fieldError}>Switched off for this deployment — it cannot be turned on here.</span>
            )}
          </div>
        </div>
        <p className={styles.settingsHint}>Changing the mode, environment, tenant, timezone, access, or web search starts a new chat. Mode is locked in for the life of a conversation.</p>
      </div>
    </div>
  );
}
